import { z } from 'zod'
import { changedOnSchema } from './change-made.input.js'
import { uuidSchema } from '../validators/uuid.validator.js'
import { calendarDateSchema } from '../validators/calendar-date.validator.js'
import { placedPointInputSchema, siteBoundarySchema } from '../dto/geo.dto.js'
import { QUANTITY_PATTERN } from '../helpers/quantity.js'
import { MAX_ESTIMATED_MINUTES } from '../helpers/minutes.js'
import { problemSeveritySchema } from '../enums/problem-severity.enum.js'
import {
  backflowTypeSchema,
  headTypeSchema,
  lineSizeSchema,
  meterSizeSchema,
  sensorTypeSchema,
  sourceAbsentEquipmentSchema,
  timerAbsentEquipmentSchema,
  valveSizeSchema,
  waterSourceKindSchema,
  wellComponentTypeSchema,
} from '../enums/irrigation.enum.js'
import {
  BOUNDARY_PROBLEM_MESSAGES,
  findBoundaryProblem,
} from '../validators/polygon.validator.js'

/*
 * Writing a site's irrigation system, which happens through one offline sync
 * batch the way trees do (see docs/field-survey.md, "Local-first"). Every
 * record carries the id the phone generated, and every upsert states the whole
 * record: an intention ("this valve should look like this"), never a delta,
 * so replaying a batch twice is the same as replaying it once.
 *
 * No `.default()` anywhere in the record schemas. An upsert states the whole
 * record, so a missing key is a malformed request, not "leave it alone" —
 * and the `.partial()` trap in field-survey.md cannot arise.
 *
 * Whatever this file refuses, the phone must refuse before queuing: one
 * refused operation fails the whole batch, and keeps failing every retry.
 * `findIrrigationViolations` in lib/src/helpers/irrigation.ts is the other
 * half of that agreement, for the rules that need the whole site.
 */

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value === '' ? null : value))
    .nullable()

const idSchema = uuidSchema

/** HCP's material ids look like `pbmat_<32 hex>`; bounded rather than pinned. */
const materialIdSchema = z.string().trim().min(1).max(100)

/** A quantity or measurement, as an exact decimal string. Never a float. */
const decimalSchema = z
  .string()
  .trim()
  .regex(QUANTITY_PATTERN, 'Enter a number, e.g. 12 or 12.5')

/** A pin on the way in. See `placedPointInputSchema`. */
const positionFields = placedPointInputSchema.shape

/**
 * What a record says it has none of, each kind once.
 *
 * Optional, against the rule above, for one reason: a phone still running a
 * build from before this field existed queues upserts without it, and a
 * batch refused for that would be refused on every retry. Absent means
 * "leave it as it is"; every current build sends it.
 */
function absentEquipmentSchema<T extends string>(kinds: z.ZodType<T>) {
  return z
    .array(kinds)
    .max(10)
    .refine(
      (values) => new Set(values).size === values.length,
      'Each kind is listed once.',
    )
    .optional()
}

const laborMinutesSchema = z
  .number()
  .int('Labor is a whole number of minutes')
  .nonnegative()
  .max(
    MAX_ESTIMATED_MINUTES,
    `Labor must be ${String(MAX_ESTIMATED_MINUTES)} minutes or less`,
  )
  .nullable()

/** Materials for a repair, each listed once. */
export const repairMaterialsSchema = z
  .array(
    z
      .object({
        materialUuid: materialIdSchema,
        quantity: decimalSchema.refine(
          (value) => Number(value) > 0,
          'A quantity must be more than zero',
        ),
      })
      .strict(),
  )
  .max(30)
  .refine(
    (materials) =>
      new Set(materials.map((item) => item.materialUuid)).size ===
      materials.length,
    'A part is listed once, with its quantity.',
  )

export const waterSourceUpsertSchema = z
  .object({
    id: idSchema,
    kind: waterSourceKindSchema,
    label: optionalText(80),
    ...positionFields,
    meterSize: meterSizeSchema.nullable(),
    staticPsi: z.number().int().min(0).max(300).nullable(),
    availableGpm: decimalSchema.nullable(),
    horsepower: decimalSchema.nullable(),
    absentEquipment: absentEquipmentSchema(sourceAbsentEquipmentSchema),
    notes: optionalText(2000),
  })
  .strict()
  .superRefine((source, context) => {
    if (source.kind === 'WELL' && source.meterSize !== null) {
      context.addIssue({
        code: 'custom',
        path: ['meterSize'],
        message: 'A well has no meter size.',
      })
    }
    if (source.kind === 'CITY_METER' && source.horsepower !== null) {
      context.addIssue({
        code: 'custom',
        path: ['horsepower'],
        message: 'A city meter has no pump.',
      })
    }
  })

export type WaterSourceUpsert = z.infer<typeof waterSourceUpsertSchema>

export const irrigationTimerUpsertSchema = z
  .object({
    id: idSchema,
    label: optionalText(40),
    ...positionFields,
    partUuid: materialIdSchema.nullable(),
    /**
     * The source its water comes from. Optional for the reason
     * `absentEquipment` is: absent leaves the link as it is.
     */
    waterSourceId: idSchema.nullable().optional(),
    absentEquipment: absentEquipmentSchema(timerAbsentEquipmentSchema),
    notes: optionalText(2000),
  })
  .strict()

export type IrrigationTimerUpsert = z.infer<typeof irrigationTimerUpsertSchema>

/** A zone outline gets the checks an area's does: a crossed ring has no inside. */
const zoneBoundarySchema = siteBoundarySchema.superRefine(
  (boundary, context) => {
    const problem = findBoundaryProblem(boundary.vertices)
    if (problem === null) return
    context.addIssue({
      code: 'custom',
      message: BOUNDARY_PROBLEM_MESSAGES[problem],
      path: ['vertices'],
    })
  },
)

/** The highest zone number a timer can have. Two-wire timers run to the hundreds. */
export const MAX_ZONE_NUMBER = 999

export const zoneUpsertSchema = z
  .object({
    id: idSchema,
    timerId: idSchema,
    /** The number on the timer. */
    number: z
      .number()
      .int()
      .min(1, 'Zones are numbered from 1')
      .max(MAX_ZONE_NUMBER),
    boundary: zoneBoundarySchema.nullable(),
    headCounts: z
      .array(
        z
          .object({
            type: headTypeSchema,
            count: z.number().int().min(0).max(999),
          })
          .strict(),
      )
      .max(6)
      .refine(
        (counts) =>
          new Set(counts.map((entry) => entry.type)).size === counts.length,
        'Each kind of head is counted once.',
      ),
    notes: optionalText(2000),
  })
  .strict()

export type ZoneUpsert = z.infer<typeof zoneUpsertSchema>

const equipmentBase = {
  id: idSchema,
  ...positionFields,
  /** The model installed, from the Housecall Pro price book. Null: not identified. */
  partUuid: materialIdSchema.nullable(),
  notes: optionalText(2000),
}

/**
 * The valve box something sits in, on the kinds that can (see
 * `BOXABLE_EQUIPMENT_KINDS`). Optional for the reason `absentEquipment` is: a
 * build from before boxes sends none, and absent leaves it in whatever box
 * it is in.
 */
const boxField = { boxId: idSchema.nullable().optional() }

/** The most splices a box is recorded with. A count, not an inventory. */
export const MAX_SPLICE_COUNT = 99

/**
 * One piece of equipment, held to its own kind's fields. A field that belongs
 * to another kind is refused (`.strict()`), not ignored: a backflow serial
 * number sent on a valve is a bug on the phone, and silently dropping it would
 * hide one.
 */
export const irrigationEquipmentUpsertSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('VALVE'),
      ...equipmentBase,
      ...boxField,
      /** Null until somebody runs the zones and finds out which this is. */
      zoneId: idSchema.nullable(),
      size: valveSizeSchema.nullable(),
      decoderUuid: materialIdSchema.nullable(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('VALVE_BOX'),
      ...equipmentBase,
      spliceCount: z.number().int().min(0).max(MAX_SPLICE_COUNT),
    })
    .strict(),
  z
    .object({
      kind: z.literal('MASTER_VALVE'),
      ...equipmentBase,
      ...boxField,
      timerId: idSchema.nullable(),
      waterSourceId: idSchema.nullable(),
      size: valveSizeSchema.nullable(),
      decoderUuid: materialIdSchema.nullable(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('BACKFLOW'),
      ...equipmentBase,
      waterSourceId: idSchema.nullable(),
      backflowType: backflowTypeSchema.nullable(),
      size: lineSizeSchema.nullable(),
      serialNumber: optionalText(60),
      lastTestedOn: calendarDateSchema.nullable(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('BALL_VALVE'),
      ...equipmentBase,
      ...boxField,
      waterSourceId: idSchema.nullable(),
      size: lineSizeSchema.nullable(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('SENSOR'),
      ...equipmentBase,
      ...boxField,
      timerId: idSchema.nullable(),
      sensorType: sensorTypeSchema.nullable(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('WELL_COMPONENT'),
      ...equipmentBase,
      /** Required: a well component is part of a well. */
      waterSourceId: idSchema,
      wellComponentType: wellComponentTypeSchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('OTHER'),
      ...equipmentBase,
      ...boxField,
      name: z.string().trim().min(1, 'Say what it is').max(80),
    })
    .strict(),
])

export type IrrigationEquipmentUpsert = z.infer<
  typeof irrigationEquipmentUpsertSchema
>

/** What a problem is on: exactly one thing. */
export const irrigationProblemSubjectInputSchema = z.discriminatedUnion(
  'kind',
  [
    z.object({ kind: z.literal('zone'), zoneId: idSchema }).strict(),
    z.object({ kind: z.literal('timer'), timerId: idSchema }).strict(),
    z.object({ kind: z.literal('equipment'), equipmentId: idSchema }).strict(),
    z
      .object({ kind: z.literal('waterSource'), waterSourceId: idSchema })
      .strict(),
  ],
)

export type IrrigationProblemSubjectInput = z.infer<
  typeof irrigationProblemSubjectInputSchema
>

export const irrigationProblemUpsertSchema = z
  .object({
    id: idSchema,
    /**
     * The visit it was found on. Read only when the problem is first
     * recorded: a problem never moves to another visit. Left out by a build
     * from before visits, which puts a new one on the site's current visit.
     */
    visitId: idSchema.optional(),
    /** Null is "Other", which `notes` has to describe. */
    typeId: idSchema.nullable(),
    subject: irrigationProblemSubjectInputSchema,
    /** Where, for a problem at a spot in a zone. */
    position: z.object(positionFields).strict().nullable(),
    count: z.number().int().min(1).max(999),
    severity: problemSeveritySchema.nullable(),
    notes: optionalText(2000),
    laborMinutes: laborMinutesSchema,
    materials: repairMaterialsSchema,
    /**
     * The problem on an earlier visit to the site it was brought forward
     * from ("Still here"). Kept only when it names one; left out, a problem
     * keeps the link it has.
     */
    previousId: idSchema.nullish(),
  })
  .strict()
  .superRefine((problem, context) => {
    if (problem.typeId === null && problem.notes === null) {
      context.addIssue({
        code: 'custom',
        path: ['notes'],
        message: 'Say what the problem is.',
      })
    }
    if (problem.position !== null && problem.subject.kind !== 'zone') {
      context.addIssue({
        code: 'custom',
        path: ['position'],
        message: 'Only a problem at a spot in a zone has its own pin.',
      })
    }
  })

export type IrrigationProblemUpsert = z.infer<
  typeof irrigationProblemUpsertSchema
>

export const IRRIGATION_ENTITIES = [
  'waterSource',
  'timer',
  'zone',
  'equipment',
  'problem',
] as const
export const irrigationEntitySchema = z.enum(IRRIGATION_ENTITIES)
export type IrrigationEntity = z.infer<typeof irrigationEntitySchema>

/**
 * How a change to the system was made, for its history: the visit it was made
 * in (null for none open), and the answer to "Was it wrong, or did it
 * change?" when a revisit asked it. On an archive, true is "Recorded by
 * mistake" and false is "Gone". Both left out by a build from before the
 * question. Ignored on a problem, which is a finding and has no history.
 */
const changeFields = {
  visitId: idSchema.nullable().optional(),
  isCorrection: z.boolean().optional(),
  /** When it changed, for a change made with no visit open. */
  changedOn: changedOnSchema,
}

/** One queued intention. Upserts state the whole record; an archive names it. */
export const irrigationSyncOperationSchema = z.discriminatedUnion('op', [
  z
    .object({
      op: z.literal('upsertWaterSource'),
      waterSource: waterSourceUpsertSchema,
      ...changeFields,
    })
    .strict(),
  z
    .object({
      op: z.literal('upsertTimer'),
      timer: irrigationTimerUpsertSchema,
      ...changeFields,
    })
    .strict(),
  z
    .object({
      op: z.literal('upsertZone'),
      zone: zoneUpsertSchema,
      ...changeFields,
    })
    .strict(),
  z
    .object({
      op: z.literal('upsertEquipment'),
      equipment: irrigationEquipmentUpsertSchema,
      ...changeFields,
    })
    .strict(),
  z
    .object({
      op: z.literal('upsertProblem'),
      problem: irrigationProblemUpsertSchema,
    })
    .strict(),
  z
    .object({
      op: z.literal('archive'),
      entity: irrigationEntitySchema,
      id: idSchema,
      ...changeFields,
    })
    .strict(),
])

export type IrrigationSyncOperation = z.infer<
  typeof irrigationSyncOperationSchema
>

/**
 * The most a batch carries. The phone sends larger queues in pieces, so this
 * is a bound on one request rather than on how much can be recorded offline.
 */
export const MAX_IRRIGATION_SYNC_OPERATIONS = 500

export const syncIrrigationInputSchema = z
  .object({
    operations: z
      .array(irrigationSyncOperationSchema)
      .max(MAX_IRRIGATION_SYNC_OPERATIONS),
    /**
     * Whose problems the answer shows: the visit this phone is working in, so
     * a visit another phone starts cannot swap the problems under it. Left
     * out, the site's current visit.
     */
    visitId: idSchema.nullable().optional(),
  })
  .strict()

export type SyncIrrigationInput = z.infer<typeof syncIrrigationInputSchema>
